/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import LayoutStyles from '../../Components/Layout/Styles/LayoutStyles';
import SearchBar from './SearchBar';

const PHONE_DISPLAY = '03 59 00 00 00';
const PHONE_TEL = '+33359000000';

const serif = 'Georgia, "Iowan Old Style", "Palatino Linotype", "Times New Roman", serif';

const C = {
  green: '#2E6B43',
  greenDark: '#235235',
  greenSoft: '#EAF3E8',
  greenLine: '#CFE3CB',
  cream: '#FBF6EC',
  creamDeep: '#F3EAD7',
  sun: '#F2B441',
  sunDeep: '#E09A1E',
  sky: '#DCEBF2',
  ink: '#2A2A26',
  inkSoft: '#5C5C53',
  white: '#FFFFFF',
};

const useStyles = makeStyles(() => ({
  root: { backgroundColor: C.cream, color: C.ink, fontSize: 19, lineHeight: 1.7 },
  wrap: {
    maxWidth: 1260,
    margin: '0 auto',
    paddingLeft: 'clamp(1.5rem, 6vw, 5rem)',
    paddingRight: 'clamp(1.5rem, 6vw, 5rem)',
  },

  demoNote: {
    backgroundColor: C.creamDeep,
    color: C.inkSoft,
    textAlign: 'center',
    fontSize: '0.95rem',
    padding: '0.7rem 1rem',
  },

  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontWeight: 700,
    fontSize: '1.15rem',
    textDecoration: 'none',
    cursor: 'pointer',
    border: 'none',
    padding: '1rem 1.7rem',
    borderRadius: 16,
    lineHeight: 1.2,
    transition: 'transform .12s ease, background .2s ease',
    '&:active': { transform: 'translateY(1px)' },
  },
  btnSun: {
    backgroundColor: C.sun,
    color: C.ink,
    boxShadow: '0 10px 24px -12px rgba(224,154,30,.8)',
    '&:hover': { backgroundColor: C.sunDeep },
  },
  btnGreen: {
    backgroundColor: C.green,
    color: '#fff',
    '&:hover': { backgroundColor: C.greenDark },
  },
  btnGhost: {
    backgroundColor: C.white,
    color: C.greenDark,
    border: `2px solid ${C.greenLine}`,
    '&:hover': { borderColor: C.green },
  },

  hero: {
    ...LayoutStyles.navbarPaddingTop,
    paddingBottom: 'clamp(2rem, 4vw, 3rem)',
    paddingTop: 'clamp(3rem, 6vw, 5.5rem)',
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1.05fr 0.95fr',
    gap: 'clamp(2.5rem, 6vw, 5.5rem)',
    alignItems: 'center',
    '@media (max-width: 880px)': { gridTemplateColumns: '1fr' },
  },
  heroText: { '@media (max-width: 880px)': { order: 2 } },
  heroArtWrap: { '@media (max-width: 880px)': { order: 1 } },
  kicker: {
    display: 'inline-block',
    backgroundColor: C.greenSoft,
    color: C.greenDark,
    fontWeight: 700,
    fontSize: '1rem',
    padding: '0.4rem 1rem',
    borderRadius: 999,
  },
  h1: {
    fontFamily: serif,
    fontWeight: 700,
    lineHeight: 1.12,
    fontSize: 'clamp(2.4rem, 5.5vw, 3.7rem)',
    marginTop: '1.1rem',
    letterSpacing: '-0.01em',
  },
  h1u: { color: C.green },
  lede: {
    fontSize: 'clamp(1.18rem, 2.2vw, 1.45rem)',
    color: C.inkSoft,
    marginTop: '1.4rem',
    maxWidth: '30ch',
  },
  heroActions: { display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '2rem' },
  heroReassure: {
    marginTop: '1.6rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    color: C.greenDark,
    fontWeight: 600,
  },
  heroArt: {
    backgroundColor: C.white,
    borderRadius: 26,
    overflow: 'hidden',
    boxShadow: '0 24px 50px -30px rgba(35,82,53,.5)',
    border: `1px solid ${C.greenLine}`,
    '& svg': { display: 'block', width: '100%', height: 'auto' },
  },

  searchCard: {
    backgroundColor: C.white,
    border: `1px solid ${C.greenLine}`,
    borderRadius: 22,
    padding: 'clamp(1.4rem, 3vw, 2.2rem)',
    marginTop: 'clamp(2rem, 4vw, 3rem)',
    boxShadow: '0 18px 40px -28px rgba(35,82,53,.5)',
  },
  searchTitle: { fontFamily: serif, fontWeight: 700, fontSize: '1.6rem', lineHeight: 1.15 },
  searchSub: { color: C.inkSoft, marginTop: '0.4rem', fontSize: '1.05rem' },
  searchForm: { marginTop: '0.5rem' },

  section: { paddingTop: 'clamp(3.5rem, 7vw, 6rem)', paddingBottom: 'clamp(3.5rem, 7vw, 6rem)' },
  sectionTitle: {
    textAlign: 'center',
    maxWidth: '24ch',
    margin: '0 auto',
    fontFamily: serif,
    fontWeight: 700,
    fontSize: 'clamp(1.9rem, 4vw, 2.8rem)',
    lineHeight: 1.12,
  },
  sectionSub: {
    textAlign: 'center',
    maxWidth: '52ch',
    margin: '1rem auto 0',
    color: C.inkSoft,
    fontSize: '1.18rem',
  },

  steps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2rem',
    marginTop: '3.5rem',
    '@media (max-width: 800px)': {
      gridTemplateColumns: '1fr',
      maxWidth: '32rem',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  step: {
    backgroundColor: C.white,
    border: `1px solid ${C.greenLine}`,
    borderRadius: 22,
    padding: '2rem',
    textAlign: 'center',
  },
  stepIco: {
    width: 84,
    height: 84,
    margin: '0 auto 1.2rem',
    borderRadius: '50%',
    backgroundColor: C.greenSoft,
    display: 'grid',
    placeItems: 'center',
  },
  stepN: {
    display: 'inline-block',
    fontFamily: serif,
    fontWeight: 700,
    color: C.sunDeep,
    fontSize: '1.1rem',
  },
  stepH: {
    fontFamily: serif,
    fontWeight: 700,
    fontSize: '1.45rem',
    marginTop: '0.2rem',
    lineHeight: 1.15,
  },
  stepP: { color: C.inkSoft, marginTop: '0.6rem', fontSize: '1.08rem' },

  band: { backgroundColor: C.green, color: '#fff' },
  bandGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1.5rem 2rem',
    paddingTop: 'clamp(2.5rem, 5vw, 3.5rem)',
    paddingBottom: 'clamp(2.5rem, 5vw, 3.5rem)',
    '@media (max-width: 760px)': { gridTemplateColumns: '1fr 1fr' },
  },
  promise: { textAlign: 'center' },
  promiseIco: {
    width: 64,
    height: 64,
    margin: '0 auto 0.9rem',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,.14)',
    display: 'grid',
    placeItems: 'center',
  },
  promiseBig: { fontFamily: serif, fontWeight: 700, fontSize: '1.55rem' },
  promiseSmall: { fontSize: '1rem', color: 'rgba(255,255,255,.85)', marginTop: '0.3rem' },

  quoteSec: { backgroundColor: C.creamDeep },
  quote: { maxWidth: '40rem', margin: '0 auto', textAlign: 'center' },
  face: { width: 88, height: 88, borderRadius: '50%', margin: '0 auto 1.5rem', display: 'block' },
  blockquote: {
    fontFamily: serif,
    fontSize: 'clamp(1.5rem, 3.4vw, 2.1rem)',
    lineHeight: 1.4,
    margin: 0,
  },
  who: {
    marginTop: '1.4rem',
    fontWeight: 700,
    color: C.greenDark,
    fontSize: '1.1rem',
    '& span': { display: 'block', fontWeight: 400, color: C.inkSoft, fontSize: '1rem' },
  },

  ctaSplit: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2.2rem',
    '@media (max-width: 800px)': { gridTemplateColumns: '1fr' },
  },
  ctaCard: { borderRadius: 22, padding: '2.4rem' },
  ctaNeed: { backgroundColor: C.greenSoft, border: `1px solid ${C.greenLine}` },
  ctaDrive: { backgroundColor: C.ink, color: '#fff' },
  ctaH: { fontFamily: serif, fontWeight: 700, fontSize: '1.7rem' },
  ctaP: { marginTop: '0.8rem', fontSize: '1.1rem' },

  callout: {
    backgroundColor: C.white,
    border: `2px solid ${C.greenLine}`,
    borderRadius: 26,
    padding: 'clamp(2rem, 4vw, 3rem)',
    textAlign: 'center',
  },
  calloutLab: { fontSize: '1.2rem', color: C.inkSoft },
  calloutNum: {
    fontFamily: serif,
    fontWeight: 700,
    fontSize: 'clamp(2.4rem, 7vw, 3.6rem)',
    color: C.greenDark,
    margin: '0.5rem 0',
  },
  calloutFree: {
    display: 'inline-block',
    backgroundColor: C.sun,
    color: C.ink,
    fontWeight: 700,
    padding: '0.3rem 1rem',
    borderRadius: 999,
  },

  footer: {
    backgroundColor: C.greenDark,
    color: 'rgba(255,255,255,.78)',
    paddingTop: 'clamp(2.5rem, 5vw, 3.5rem)',
    paddingBottom: 'clamp(2.5rem, 5vw, 3.5rem)',
  },
  footerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1.5rem 2.5rem',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontFamily: serif,
    fontSize: '1.3rem',
    color: '#fff',
  },
}));

const Check = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={C.greenDark}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function Home() {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.demoNote}>
        Site de démonstration — les coordonnées et trajets sont fictifs.
      </div>

      {/* HERO + recherche transport */}
      <section className={classes.hero}>
        <div className={`${classes.wrap} ${classes.heroGrid}`}>
          <div className={classes.heroText}>
            <span className={classes.kicker}>Transport solidaire dans le Nord</span>
            <h1 className={classes.h1}>
              Vous n'avez pas de voiture ?<br />
              <span className={classes.h1u}>On vient vous chercher.</span>
            </h1>
            <p className={classes.lede}>
              Un voisin bénévole vous emmène à vos rendez-vous, au marché, voir votre famille — puis
              vous ramène chez vous.
            </p>
            <div className={classes.heroActions}>
              <a className={`${classes.btn} ${classes.btnSun}`} href={`tel:${PHONE_TEL}`}>
                Appeler pour un trajet
              </a>
              <a className={`${classes.btn} ${classes.btnGhost}`} href="#conduire">
                Je veux conduire
              </a>
            </div>
            <p className={classes.heroReassure}>
              <Check /> C'est gratuit, et un humain vous répond.
            </p>
          </div>

          <div className={classes.heroArtWrap}>
            <div className={classes.heroArt}>
              <svg
                viewBox="0 0 520 480"
                role="img"
                aria-label="Une voiture roule sur une petite route de campagne ensoleillée"
              >
                <defs>
                  <linearGradient id="skyDemo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={C.cream} />
                    <stop offset="1" stopColor={C.sky} />
                  </linearGradient>
                </defs>
                <rect width="520" height="480" fill="url(#skyDemo)" />
                <circle cx="420" cy="92" r="40" fill={C.sun} />
                <g stroke={C.sun} strokeWidth="5" strokeLinecap="round" opacity="0.7">
                  <line x1="420" y1="22" x2="420" y2="38" />
                  <line x1="486" y1="92" x2="470" y2="92" />
                  <line x1="468" y1="44" x2="457" y2="55" />
                  <line x1="372" y1="44" x2="383" y2="55" />
                </g>
                <path d="M0 300 Q 130 240 260 290 T 520 280 V480 H0 Z" fill="#A9D0A2" />
                <path d="M0 350 Q 160 300 330 345 T 520 340 V480 H0 Z" fill="#7DBB7E" />
                <g>
                  <rect x="78" y="276" width="9" height="34" rx="3" fill="#8a6a44" />
                  <circle cx="82" cy="266" r="26" fill="#4F9A5A" />
                  <rect x="430" y="300" width="8" height="28" rx="3" fill="#8a6a44" />
                  <circle cx="434" cy="292" r="20" fill="#5BA36B" />
                </g>
                <path d="M0 380 Q 200 350 520 390 V480 H0 Z" fill={C.green} />
                <path
                  d="M120 480 C 200 420, 150 400, 270 380 C 380 362, 320 348, 410 332"
                  fill="none"
                  stroke="#EFE7D2"
                  strokeWidth="46"
                  strokeLinecap="round"
                />
                <path
                  d="M120 480 C 200 420, 150 400, 270 380 C 380 362, 320 348, 410 332"
                  fill="none"
                  stroke={C.sun}
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="10 16"
                  opacity="0.85"
                />
                <g transform="translate(196 372) rotate(-12)">
                  <rect x="-34" y="-20" width="68" height="24" rx="11" fill={C.green} />
                  <path d="M-22 -20 q 6 -16 22 -16 h 8 q 12 0 16 16 z" fill="#3f8a55" />
                  <circle cx="-8" cy="-26" r="6.5" fill={C.sky} />
                  <circle cx="8" cy="-26" r="6.5" fill={C.sky} />
                  <circle cx="-18" cy="8" r="9" fill={C.ink} />
                  <circle cx="18" cy="8" r="9" fill={C.ink} />
                  <circle cx="-18" cy="8" r="4" fill="#7DBB7E" />
                  <circle cx="18" cy="8" r="4" fill="#7DBB7E" />
                </g>
              </svg>
            </div>
          </div>
        </div>

        {/* Carte de demande de trajet en ligne (vrai moteur transport) */}
        <div className={classes.wrap}>
          <div className={classes.searchCard}>
            <h2 className={classes.searchTitle}>Ou demandez votre trajet en ligne</h2>
            <p className={classes.searchSub}>
              Indiquez votre départ et votre destination, puis le jour et l'heure du trajet.
            </p>
            <SearchBar className={classes.searchForm} />
          </div>
        </div>
      </section>

      {/* COMMENT ÇA SE PASSE */}
      <section className={classes.section}>
        <div className={classes.wrap}>
          <h2 className={classes.sectionTitle}>Comment ça se passe</h2>
          <p className={classes.sectionSub}>
            Pas besoin d'ordinateur ni de smartphone. Vous appelez, on s'occupe du reste.
          </p>
          <div className={classes.steps}>
            <div className={classes.step}>
              <div className={classes.stepIco}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.green}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6.6 10.8a13 13 0 0 0 5.6 5.6l1.9-1.9a1.3 1.3 0 0 1 1.3-.3 9 9 0 0 0 2.8.45 1.3 1.3 0 0 1 1.3 1.3V19a1.3 1.3 0 0 1-1.3 1.3A16 16 0 0 1 3.7 5.3 1.3 1.3 0 0 1 5 4h2.4a1.3 1.3 0 0 1 1.3 1.3 9 9 0 0 0 .45 2.8 1.3 1.3 0 0 1-.3 1.3z" />
                </svg>
              </div>
              <span className={classes.stepN}>Étape 1</span>
              <h3 className={classes.stepH}>Vous appelez</h3>
              <p className={classes.stepP}>
                Dites-nous où vous voulez aller et quel jour. Un conseiller note tout avec vous.
              </p>
            </div>
            <div className={classes.step}>
              <div className={classes.stepIco}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.green}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21a8 8 0 0 1 16 0" />
                </svg>
              </div>
              <span className={classes.stepN}>Étape 2</span>
              <h3 className={classes.stepH}>Un voisin vient</h3>
              <p className={classes.stepP}>
                Un conducteur bénévole proche de chez vous accepte de vous accompagner.
              </p>
            </div>
            <div className={classes.step}>
              <div className={classes.stepIco}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.green}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 16l1.5-5h11L19 16" />
                  <circle cx="7.5" cy="16" r="1.8" />
                  <circle cx="16.5" cy="16" r="1.8" />
                  <path d="M3 16h18" />
                </svg>
              </div>
              <span className={classes.stepN}>Étape 3</span>
              <h3 className={classes.stepH}>Il vous emmène et vous ramène</h3>
              <p className={classes.stepP}>
                De votre porte jusqu'à destination, et retour. En toute tranquillité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <div className={classes.band}>
        <div className={`${classes.wrap} ${classes.bandGrid}`}>
          {[
            { big: 'Partout dans le Nord', small: 'Avesnois, Pays de Mormal et alentours' },
            { big: "C'est gratuit", small: 'Rien à payer pour le passager' },
            { big: 'Des bénévoles', small: 'Des voisins de confiance, près de chez vous' },
            { big: 'Porte à porte', small: 'On vient vous chercher chez vous' },
          ].map(p => (
            <div className={classes.promise} key={p.big}>
              <div className={classes.promiseIco}>
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 21s-7-4.5-7-10a7 7 0 0 1 14 0c0 5.5-7 10-7 10z" />
                  <circle cx="12" cy="11" r="2.5" />
                </svg>
              </div>
              <div className={classes.promiseBig}>{p.big}</div>
              <div className={classes.promiseSmall}>{p.small}</div>
            </div>
          ))}
        </div>
      </div>

      {/* TÉMOIGNAGE */}
      <section className={`${classes.section} ${classes.quoteSec}`}>
        <div className={`${classes.wrap} ${classes.quote}`}>
          <svg className={classes.face} viewBox="0 0 88 88" aria-hidden="true">
            <circle cx="44" cy="44" r="44" fill={C.sun} />
            <circle cx="44" cy="36" r="15" fill="#fff" />
            <path d="M18 80a26 26 0 0 1 52 0z" fill="#fff" />
          </svg>
          <blockquote className={classes.blockquote}>
            « Avant, je ne pouvais plus aller au marché. Maintenant, Bernard m'emmène tous les
            mardis. »
          </blockquote>
          <div className={classes.who}>
            Marcelle, 81 ans<span>Le Quesnoy</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={classes.section} id="conduire">
        <div className={classes.wrap}>
          <div className={classes.ctaSplit}>
            <div className={`${classes.ctaCard} ${classes.ctaNeed}`}>
              <h3 className={classes.ctaH}>Vous avez besoin d'un trajet</h3>
              <p className={classes.ctaP} style={{ color: C.inkSoft }}>
                Pour vos courses, vos rendez-vous chez le médecin, ou simplement voir vos proches.
                Appelez, on trouve une solution avec vous.
              </p>
              <a
                className={`${classes.btn} ${classes.btnGreen}`}
                style={{ marginTop: '1.6rem' }}
                href={`tel:${PHONE_TEL}`}
              >
                Appeler le {PHONE_DISPLAY}
              </a>
            </div>
            <div className={`${classes.ctaCard} ${classes.ctaDrive}`}>
              <h3 className={classes.ctaH} style={{ color: '#fff' }}>
                Vous aimez rendre service
              </h3>
              <p className={classes.ctaP} style={{ color: 'rgba(255,255,255,.82)' }}>
                Devenez conducteur bénévole. Vous choisissez vos disponibilités, vous êtes assuré,
                et vos frais d'essence sont remboursés.
              </p>
              <a
                className={`${classes.btn} ${classes.btnSun}`}
                style={{ marginTop: '1.6rem' }}
                href={`tel:${PHONE_TEL}`}
              >
                Devenir conducteur
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* APPEL */}
      <section className={classes.section} style={{ paddingTop: 0 }}>
        <div className={classes.wrap}>
          <div className={classes.callout}>
            <p className={classes.calloutLab}>Une question ? Un trajet à prévoir ?</p>
            <p className={classes.calloutNum}>{PHONE_DISPLAY}</p>
            <span className={classes.calloutFree}>
              Appel gratuit · du lundi au vendredi, 9h–17h
            </span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className={classes.footer}>
        <div className={`${classes.wrap} ${classes.footerRow}`}>
          <div>
            <div className={classes.footerBrand}>Démo Transport</div>
            <p style={{ marginTop: '0.8rem', fontSize: '0.95rem', lineHeight: 1.7 }}>
              Transport solidaire · Département du Nord
              <br />
              Une démonstration propulsée par Wello
            </p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.95rem', lineHeight: 1.7 }}>
            <strong style={{ color: '#fff', fontSize: '1.1rem' }}>{PHONE_DISPLAY}</strong>
            <br />
            demo-transport.fr · données fictives
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
